import React, { useEffect, useMemo, useState } from 'react'
import { adminService, type Permission } from '@/services/adminService'
import { PageHeader } from '@/components/layout'

interface AdminUserRow {
  id: string
  name: string
  email: string
  role: string
}

interface AdminPrivilegeManagementPageProps {
  // Injected in tests; in production the page uses the module-level adminService.
  service?: typeof adminService
  fetchAdminUsers?: () => Promise<AdminUserRow[]>
}

const defaultFetchAdminUsers = async (): Promise<AdminUserRow[]> => {
  // Lazy import to avoid bloating the admin bundle when privileges page isn't used.
  const { apiClient } = await import('@/services/api')
  const res = await apiClient.get<{ users: AdminUserRow[] } | AdminUserRow[]>('/admin/users', {
    params: { role: 'admin' },
  })
  const data = res.data as AdminUserRow[] | { users: AdminUserRow[] }
  if (Array.isArray(data)) return data
  return (data as { users: AdminUserRow[] }).users || []
}

export const AdminPrivilegeManagementPage: React.FC<AdminPrivilegeManagementPageProps> = ({
  service = adminService,
  fetchAdminUsers = defaultFetchAdminUsers,
}) => {
  const [catalog, setCatalog] = useState<Record<string, Permission[]>>({})
  const [admins, setAdmins] = useState<AdminUserRow[]>([])
  const [search, setSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState<AdminUserRow | null>(null)
  const [granted, setGranted] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Load permission catalog + admin users on mount.
  useEffect(() => {
    let mounted = true
    Promise.all([service.listPermissions(), fetchAdminUsers()])
      .then(([permsRes, usersList]) => {
        if (!mounted) return
        if (permsRes.success && permsRes.data) {
          setCatalog(permsRes.data.modules || {})
        }
        setAdmins(usersList)
      })
      .catch((err) => {
        setToast({ type: 'error', text: err?.message || 'Gagal memuat data' })
      })
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [service, fetchAdminUsers])

  // Load the selected user's granted permissions.
  useEffect(() => {
    if (!selectedUser) return
    let mounted = true
    service
      .getUserPermissions(selectedUser.id)
      .then((res) => {
        if (!mounted) return
        if (res.success && res.data) {
          setGranted(new Set(res.data.permissions || []))
        }
      })
      .catch((err) => setToast({ type: 'error', text: err?.message || 'Gagal memuat izin' }))
    return () => {
      mounted = false
    }
  }, [service, selectedUser])

  const filteredAdmins = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return admins
    return admins.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [admins, search])

  const toggleCode = (code: string) => {
    setGranted((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const handleSave = async () => {
    if (!selectedUser) return
    const confirmed = window.confirm(
      `Apakah Anda yakin ingin mengubah permission untuk ${selectedUser.name}? Perubahan akan langsung berlaku.`
    )
    if (!confirmed) return
    setSaving(true)
    try {
      const res = await service.updateUserPermissions(selectedUser.id, Array.from(granted))
      if (!res.success) throw new Error(res.error?.message || 'Gagal menyimpan')
      setToast({ type: 'success', text: 'Izin tersimpan' })
    } catch (err) {
      const e = err as Error
      setToast({ type: 'error', text: e.message || 'Gagal menyimpan' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="admin-privileges-page">
      <PageHeader
        title="Manajemen Hak Akses"
        subtitle="Kelola izin dan otoritas untuk setiap akun administrator"
      />

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          data-testid="privileges-toast"
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            toast.type === 'success'
              ? 'bg-emerald-500 text-white'
              : 'bg-rose-500 text-white'
          }`}
        >
          <span className="material-symbols-outlined text-[20px]">
            {toast.type === 'success' ? 'check_circle' : 'error'}
          </span>
          <span className="font-medium">{toast.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User list */}
        <div className="lg:col-span-1 bg-white dark:bg-surface-card rounded-lg border border-slate-200 dark:border-white/5 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-280px)]">
          <div className="p-5 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-1">Cari Administrator</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[18px]">search</span>
              <input
                type="search"
                placeholder="Nama atau email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:border-primary/50 transition-all shadow-inner"
                data-testid="privileges-search"
              />
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-white/5" data-testid="admin-list">
            {filteredAdmins.length === 0 && (
              <li className="p-8 text-center text-sm text-slate-500 dark:text-slate-400 italic">Tidak ada admin ditemukan.</li>
            )}
            {filteredAdmins.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className={`w-full text-left py-4 px-5 transition-all relative ${
                    selectedUser?.id === u.id 
                      ? 'bg-primary/5' 
                      : 'hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                  onClick={() => setSelectedUser(u)}
                  data-testid={`admin-row-${u.id}`}
                >
                  {selectedUser?.id === u.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full shadow-[2px_0_10px_rgba(204,0,1,0.3)]"></div>
                  )}
                  <div className={`font-bold text-sm ${selectedUser?.id === u.id ? 'text-primary' : 'text-slate-900 dark:text-white'}`}>
                    {u.name}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{u.email}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Permissions panel */}
        <div className="lg:col-span-2 bg-white dark:bg-surface-card rounded-lg border border-slate-200 dark:border-white/5 shadow-sm flex flex-col h-[calc(100vh-280px)]">
          {!selectedUser ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-slate-500 dark:text-slate-400 bg-slate-50/30 dark:bg-transparent">
              <div className="size-20 rounded-2xl bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-4xl opacity-30">admin_panel_settings</span>
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-2">Pilih Administrator</h3>
              <p className="max-w-xs text-sm">Silakan pilih pengguna dari daftar di sebelah kiri untuk mulai mengelola hak akses mereka.</p>
            </div>
          ) : (
            <>
              <div className="p-6 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02] flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">{selectedUser.name}</h2>
                    <span className="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-white/10 text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">{selectedUser.role}</span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedUser.email}</p>
                </div>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                  data-testid="privileges-save"
                >
                  <span className="material-symbols-outlined text-[18px]">save</span>
                  {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-8">
                {Object.keys(catalog).sort().map((mod) => (
                  <div key={mod} className="space-y-4">
                    <div className="flex items-center gap-3">
                      <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{mod}</h4>
                      <div className="h-[1px] flex-1 bg-slate-100 dark:bg-white/5"></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {catalog[mod].map((p) => {
                        const isChecked = granted.has(p.code)
                        return (
                          <label 
                            key={p.code} 
                            className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer group ${
                              isChecked 
                                ? 'bg-primary/5 border-primary/20 dark:border-primary/20' 
                                : 'bg-transparent border-slate-100 dark:border-white/5 hover:border-slate-200 dark:hover:border-white/10'
                            }`}
                          >
                            <div className="relative flex items-center pt-0.5">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleCode(p.code)}
                                className="w-5 h-5 rounded border-slate-300 dark:border-white/20 text-primary accent-primary focus:ring-primary cursor-pointer"
                                data-testid={`perm-${p.code}`}
                              />
                            </div>
                            <div className="flex flex-col">
                              <span className={`text-sm font-bold transition-colors ${isChecked ? 'text-primary' : 'text-slate-700 dark:text-slate-200'}`}>
                                {p.code}
                              </span>
                              <span className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight mt-0.5" title={p.description}>
                                {p.description || 'Kelola fitur dan akses untuk modul ini'}
                              </span>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminPrivilegeManagementPage
